import { BookMarked, Users, Scale } from "lucide-react";
import { useAddToGlossLink, AddButton, AfterAddHelp } from "@/components/AddToGlossLink";
import { VerifiedBadge } from "@/components/VerifyPack";

export const StatusPill = ({ status, testId }) => {
  const cls = {
    Published: "bg-emerald-100 text-emerald-800",
    "Needs review": "bg-amber-100 text-amber-800",
    Draft: "bg-slate-200 text-slate-700",
    Archived: "bg-stone-200 text-stone-600",
  }[status];
  return <span data-testid={testId} className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${cls}`}>{status}</span>;
};

export const EditionPill = ({ label, testId }) => (
  <span data-testid={testId} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${label === "Latest published edition" ? "border-blue-300 text-blue-800 bg-blue-50" : "border-slate-300 text-slate-600"}`}>{label}</span>
);

export const PackCard = ({ pack, onPreview, index }) => {
  const { sent, busy, add } = useAddToGlossLink(pack);
  return (
    <article data-testid={`pack-card-${pack.id}`} style={{ animationDelay: `${index * 60}ms` }}
      className="gl-fade-up flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-lg">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="rounded-md bg-slate-900 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-white">{pack.subject}</span>
        <EditionPill label={pack.edition_label} testId={`pack-card-edition-label-${pack.id}`} />
        <VerifiedBadge verification={pack.verification} testId={`pack-card-verified-${pack.id}`} />
      </div>
      <h3 className="text-2xl font-extrabold leading-tight tracking-tight text-slate-900">{pack.title}</h3>
      <p className="mt-2 text-sm text-slate-600">{pack.textbook_title} · {pack.edition}</p>
      <div className="mt-4 space-y-2 text-sm text-slate-600">
        <div className="flex gap-2"><Users className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{pack.authors}</div>
        <div className="flex gap-2"><Scale className="h-4 w-4 shrink-0 text-slate-400" />{pack.license_name.split(" (")[0]}</div>
      </div>
      <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-slate-700" data-testid={`pack-card-glossary-name-${pack.id}`}>
        <div>In your GlossLink dropdown this pack is named <b className="text-slate-900">“{pack.glossary_name}”</b>.</div>
        <div className="mt-1">Every definition ends with <b className="text-slate-900">“Source: {pack.title.split(" — ")[0]}”</b> so you always know which book it came from.</div>
      </div>
      <div className="mt-6 flex items-end justify-between gap-3">
        <div>
          <div className="text-3xl font-extrabold text-slate-900" data-testid={`pack-card-entry-count-${pack.id}`}>{pack.entry_count}</div>
          <div className="flex items-center gap-1 text-xs text-slate-500"><BookMarked className="h-3 w-3" />terms · word for word</div>
        </div>
        <button data-testid={`pack-card-preview-button-${pack.id}`} onClick={() => onPreview(pack.id)} className="text-sm font-semibold text-blue-700 hover:underline">Preview source &amp; license</button>
      </div>
      <div className="mt-5 space-y-3">
        <AddButton pack={pack} sent={sent} busy={busy} add={add} size="lg" testId={`pack-card-add-button-${pack.id}`} />
        <AfterAddHelp pack={pack} sent={sent} />
      </div>
    </article>
  );
};
